/* Core & libraries */
import React, {useState, useRef, useEffect} from 'react';
import {useTheme} from 'react-jss';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {Formik, Field, ErrorMessage} from 'formik';

/* Helpers, constants, custom methods, internal libraries */
import {useUserContext} from "../../contexts/userContext";
import {createValidationPolicyYupSchema, useValidationService} from "../../resources/validations/validations";
import {useEscalationService} from "../../services/escalationFormService";
import {useAnalytics} from "../../hooks/useAnalytics";
import {AIRLINES, PAGE_TITLES} from "../../constants/appConstants";
import {NOT_FOUND_ROUTE} from "../../constants/routes";
import { useLocaleContext } from 'contexts/localeContext';

/* Components */
import {ReactComponent as AALogo} from '../../resources/images/aa-logo.svg';
import {ReactComponent as AALogoSmall} from "../../resources/images/aa-logo-small-no-text.svg";
import {ReactComponent as CHOICELogo} from '../../resources/images/choice-business-direct-logo.svg';
import {ReactComponent as CHOICELogoSmall} from '../../resources/images/landing-choice-logo-small.svg';
import UCSelect from "../../components/ucselect";
import UCButton from "../../components/buttons";
import EscalationHeader from "../../components/escalationPageHeader";

/* Styles */
import styles from './styles.js';
import generalStyles from '../../resources/styles/general';
import {colors} from '../../constants/colors';


const messages = defineMessages({
  topicAccountMessage: {
    id: 'escalation.topic.account',
  },
  topicDuplicateMessage: {
    id: 'escalation.topic.duplicate',
  },
  topicRewardsMessage: {
    id: 'escalation.topic.rewards',
  },
  topicCitiBusinessMessage: {
    id: 'escalation.topic.citiBusinessValue',
  }, topicOtherMessage: {
    id: 'escalation.topic.other',
  },
  questionPlaceholderMessage: {
    id: 'escalation.topic.questionPlaceholder',
  },
});

const EscalationForm = () => {
  const theme = useTheme();
  const classes = styles({theme});
  const general = generalStyles({theme});

  const formRef = useRef(null);
  const intl = useIntl();
  const {initAnalytics} = useAnalytics();
  const locale = useLocaleContext();

  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const {postEscalationForm, getTopicOptions} = useEscalationService();

  const providerConfig = {
    [AIRLINES.AA]: {
      logo: <AALogo />,
      membershipLogo: <AALogoSmall />,
      membershipLabelId: 'traveler.details.aadvantage',
      showMembershipLogo: true,
      emailLabelId: 'escalation.form.email.AA'
    },
    [AIRLINES.CHOICE]: {
      logo: <CHOICELogo />,
      membershipLogo: <CHOICELogoSmall />,
      membershipLabelId: 'traveler.details.membershipNumber.CHOICE',
      showMembershipLogo: false,
      emailLabelId: 'escalation.form.email.CHOICE'
    },
  };

  const config = providerConfig[providerCode] ?? providerConfig[AIRLINES.AA];
  const [topicOptions, setTopicOptions] = useState([]);

  useEffect(() => {
    if (providerCode && ![AIRLINES.AA, AIRLINES.CHOICE].includes(providerCode)) {
      window.location.href = NOT_FOUND_ROUTE;
    }
  }, []);

  const {createEscalationFormValidationObj} = useValidationService()
  const [testSchema, setTestSchema] = useState({});

  const advatageNumber = userDetails?.userInfo?.providerUserAssignment ? userDetails?.userInfo?.providerUserAssignment[0]?.maskedMembershipNumber
    : "";
  const customerName = userDetails?.userInfo.firstName ? userDetails?.userInfo?.firstName + " " + userDetails?.userInfo?.lastName : "";

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["escalationForm"]
    };
    initAnalytics(initUtagData, 'init call', 'view')

    getValidationSchema();
    (async () => {
      const response = await getTopicOptions(["O_ESCALATION_TOPIC"]);
      setTopicOptions(
        response
          ?.find((item) => item.optionName === "O_ESCALATION_TOPIC")
          ?.options?.map(({ optionValue, optionText }) => ({
            value: optionValue,
            labelId: optionText,
            label: optionValue,
          })) || [],
      );
    })();
  }, []);

  useEffect(() => {
    getValidationSchema()
    if (formRef.current) {
      formRef.current.resetForm()
    }
  }, [locale])

  const getValidationSchema = () => {
   //TODO: api call to get validations //simpleMockedApiValidationObj
   const result = createValidationPolicyYupSchema(createEscalationFormValidationObj);
   setTestSchema(result)
  }

  const submitForm = async (values, setSubmitting) => {
    await postEscalationForm({
      userEntityId: userDetails?.userInfo?.userEntityId,
      supportTopic: values.supportTopic,
      userMessage: values.userMessage

    })
    setSubmitting(false);
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  return (
    (providerCode && [AIRLINES.AA, AIRLINES.CHOICE].includes(providerCode)) &&
    <div className={`${general.main} ${classes.escalation}`}>
      <EscalationHeader hideSignOut={providerCode === AIRLINES.CHOICE}/>
      {config.logo}
      <div className={classes.escalationContainer}>
        <h1 className={classes.title}>
          <FormattedMessage id='escalation.form.title'/>
        </h1>
        <p className={classes.description}>
          <FormattedMessage id='escalation.form.description'/>
        </p>
      </div>
      {userDetails?.userInfo?.providerUserAssignment &&
        <div className={classes.formContainer}>
          <Formik
            innerRef={formRef}
            initialValues={{
              customerName: customerName || "",
              aadvantage: advatageNumber || "",
              email: userDetails?.userInfo?.emailAddress || "",
              companyName: userDetails?.userInfo?.companyInfo?.companyName || "",
              supportTopic: "",
              userMessage: ""
            }}
            validationSchema={testSchema}
            onSubmit={(values, {setSubmitting}) => {
              submitForm(values, setSubmitting);
            }}
          >
            {({
                values,
                errors,
                touched,
                setFieldTouched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => (
              <>
                <form onSubmit={handleSubmit}>
                  <div className={classes.inputContainer}>
                    <label htmlFor='customerName' className={classes.label}>
                      <FormattedMessage id='escalation.form.customerName'/>
                    </label>
                    <Field
                      type='text'
                      name='customerName'
                      id='customerName'
                      className={
                        errors.customerName && touched.customerName
                          ? 'input-error'
                          : null
                      }
                      disabled={true}
                    />
                    <ErrorMessage
                      name='customerName'
                      component='span'
                      className={classes.error}
                    />
                  </div>
                  <div className={`${classes.inputContainerMembership} ${classes.inputMargin}`}>
                    <label htmlFor="aadvantage" className={classes.label}>
                      <FormattedMessage id={config.membershipLabelId}/>
                    </label>
                    <div>
                      {config.showMembershipLogo && config.membershipLogo}
                      <Field
                        type="text"
                        name="aadvantage"
                        id="aadvantage"
                        className={
                          errors.aadvantage && touched.aadvantage
                            ? "input-error"
                            : null
                        }
                        disabled={true}
                      />
                    </div>
                    <ErrorMessage
                      name="aadvantage"
                      component="span"
                      className={classes.error}
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <label htmlFor='email' className={classes.label}>
                      <FormattedMessage id={config.emailLabelId}/>
                    </label>
                    <Field
                      type='text'
                      name='email'
                      id='email'
                      className={
                        errors.email && touched.email
                          ? 'input-error'
                          : null
                      }
                      disabled={true}
                    />
                    <ErrorMessage
                      name='email'
                      component='span'
                      className={classes.error}
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <label htmlFor='companyName' className={classes.label}>
                      <FormattedMessage id='escalation.form.companyName'/>
                    </label>
                    <Field
                      type='text'
                      name='companyName'
                      id='companyName'
                      className={
                        errors.companyName && touched.companyName
                          ? 'input-error'
                          : null
                      }
                      disabled={true}
                    />
                    <ErrorMessage
                      name='companyName'
                      component='span'
                      className={classes.error}
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <label htmlFor="supportTopic" className={classes.label}>
                      <FormattedMessage id="escalation.form.support"/>
                    </label>
                    <Field
                      name="supportTopic"
                      id="supportTopic"
                      render={({form}) => (
                        <div>
                          <UCSelect
                            className={classes.roles}
                            displayName={<FormattedMessage id="escalation.form.supportPlaceholder"/>}
                            options={topicOptions}
                            defaultValue={topicOptions.find(item => item.value === values.topic)}
                            onChange={(item) => form.setFieldValue("supportTopic", item.value)}
                          />
                          <ErrorMessage
                            name="supportTopic"
                            component="span"
                            className={classes.error}
                          />
                        </div>
                      )}
                      className={
                        errors.supportTopic && touched.supportTopic
                          ? "input-error"
                          : null
                      }
                    />
                  </div>
                  <div className={classes.questionInputContainer}>
                    <label htmlFor='userMessage' className={classes.label}>
                      <FormattedMessage id='escalation.topic.question'/>
                    </label>
                    <Field
                      placeholder={intl.formatMessage(
                        messages.questionPlaceholderMessage
                      )}
                      type='text'
                      as="textarea"
                      name='userMessage'
                      id='userMessage'
                      autoComplete="off"
                      className={
                        errors.userMessage && touched.userMessage
                          ? 'input-error'
                          : null
                      }
                    />
                    <ErrorMessage
                      name='userMessage'
                      component='span'
                      className={classes.error}
                    />
                  </div>
                  <div className={classes.btnContainer}>
                    <UCButton
                      className={classes.saveBtn}
                      onClick={handleFormSubmit}
                      type='primary'
                      text={
                        <FormattedMessage id='escalation.form.submit'/>
                      }
                      spinner={{
                        name: 'escalationFormSpinner',
                        size: 20,
                      }}
                    />
                  </div>
                </form>
              </>
            )}
          </Formik>
        </div>
      }
    </div>
  );
};

export default EscalationForm;
